// ─── Dashboard Tile System ───────────────────────────────
// Wraps every dashboard widget in a uniform-height tile so the grid packs
// cleanly with no wasted vertical space. Content that overflows scrolls
// internally; an Expand control opens the widget at full height in a modal.
//
// Widgets that fetch their own data report whether they are empty via
// `useReportTileEmpty`, so the parent can demote data-less tiles to the
// bottom of the grid. Summary-driven widgets are detected in the parent
// from the dashboard summary and don't need to call the hook.

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Maximize2, X } from "lucide-react";
import { cn } from "@/lib/utils";

// Uniform tile height — every widget on the grid shares this so rows never
// leave gaps under short cards. Overflow scrolls; Expand shows the rest.
export const TILE_HEIGHT_CLASS = "h-[380px]";

// ─── Empty-state reporting context ───────────────────────

type ReportFn = (widgetId: string, isEmpty: boolean) => void;

const TileReportContext = createContext<ReportFn | null>(null);

export function DashboardTileProvider({
  report,
  children,
}: {
  report: ReportFn;
  children: ReactNode;
}) {
  return (
    <TileReportContext.Provider value={report}>
      {children}
    </TileReportContext.Provider>
  );
}

/**
 * Self-fetching widgets call this with their own empty state so the dashboard
 * can demote them below populated widgets. Safe to call unconditionally —
 * pass `false` while loading or when data exists, `true` when there is no
 * meaningful content to show.
 */
export function useReportTileEmpty(widgetId: string, isEmpty: boolean) {
  const report = useContext(TileReportContext);
  useEffect(() => {
    report?.(widgetId, isEmpty);
  }, [report, widgetId, isEmpty]);
}

// ─── Tile wrapper ────────────────────────────────────────

interface DashboardTileProps {
  widgetId: string;
  label: string;
  /** Render the widget. Called once for the tile and again for the expand modal. */
  render: () => ReactNode;
  /** Demoted (data-less) tiles sink below populated ones. */
  isEmpty?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function DashboardTile({
  widgetId,
  label,
  render,
  isEmpty = false,
  className,
  style,
}: DashboardTileProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <div
        className={cn(
          "group/tile relative rounded-xl overflow-hidden",
          TILE_HEIGHT_CLASS,
          isEmpty && "opacity-[0.97]",
          className,
        )}
        style={style}
        data-widget-id={widgetId}
        data-empty={isEmpty || undefined}
      >
        {/* Scrollable content. The inner flex column is min-h-full so short
            cards stretch to fill the tile (flex-1 on the card), while tall cards
            grow past it and scroll — keeping the card background behind content.
            Only the first child stretches: some widgets render the card plus a
            sibling modal, and we must not give that sibling flex height. */}
        <div className="h-full overflow-y-auto overflow-x-hidden tile-scroll">
          {/* Render the widget in exactly ONE place at a time: the tile when
              collapsed, the modal when expanded. This avoids two live instances
              (and duplicate side effects) while the modal is open. */}
          <div className="flex min-h-full flex-col [&>*:first-child]:flex-1">{!expanded && render()}</div>
        </div>

        {/* Expand affordance — bottom-right (clear of widgets' top-right header
            actions like "View All"), revealed on hover, frosted to read over content. */}
        <button
          type="button"
          onClick={() => setExpanded(true)}
          title={`Expand ${label}`}
          aria-label={`Expand ${label}`}
          className={cn(
            "absolute bottom-2.5 right-2.5 z-20 inline-flex h-7 w-7 items-center justify-center rounded-lg",
            "bg-background/80 backdrop-blur border border-border/60 text-muted-foreground shadow-sm",
            "opacity-0 group-hover/tile:opacity-100 focus-visible:opacity-100",
            "hover:text-foreground hover:bg-background transition-all duration-200",
          )}
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto p-0 gap-0 [&>button:last-child]:hidden">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/95 backdrop-blur px-4 py-2.5">
            <span className="text-sm font-semibold text-foreground">{label}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-lg"
              onClick={() => setExpanded(false)}
              aria-label={`Close expanded ${label}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="p-4">{expanded && render()}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}
